import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ListFilterPlus } from "lucide-react";
import { globalStyleObj } from "@/app/assets/styles";
import { useUIPermissionCheck } from "@/lib/hooks";
import { RiEditBoxLine } from "react-icons/ri";
import Link from "next/link";
import { DeleteButton, SwitchButton } from "..";

// Edit Action
const GetEditAction = ({
  adminRole,
  permissionsList,
  permissionItems,
  editRoute,
}) => {
  return (
    <>
      {useUIPermissionCheck(
        adminRole,
        permissionsList,
        permissionItems.editItem
      ) && (
        <DropdownMenuItem className="flex items-center justify-between gap-2 text-[12px]">
          <span>Edit</span>
          <Link
            href={editRoute}
            className="transition-300 rounded-full bg-[#49ABE0]/20 p-2 text-[#49ABE0] hover:bg-[#49ABE0] hover:text-white"
          >
            <RiEditBoxLine size={12} />
          </Link>
        </DropdownMenuItem>
      )}
    </>
  );
};

// Delete Action
const GetDeleteAction = ({
  adminRole,
  permissionsList,
  permissionItems,
  targetType = "N/A",
  targetName = "N/A",
  deleteArgs = [],
  actionFunctions = {},
}) => {
  return (
    <>
      {useUIPermissionCheck(
        adminRole,
        permissionsList,
        permissionItems.deleteItem
      ) && (
        <DropdownMenuItem className="flex items-center justify-between gap-2 text-[12px]">
          <span>Delete</span>
          <DeleteButton
            targetType={targetType}
            targetName={targetName}
            apiCallback={actionFunctions.deleteItemFnc}
            args={deleteArgs}
          />
        </DropdownMenuItem>
      )}
    </>
  );
};

// Active Status Toggle Switch
const GetActiveStatusToggleSwitch = ({
  adminRole,
  permissionsList,
  permissionItems,
  toggleActiveArgs = [],
  activeStatus,
  actionFunctions = {},
}) => {
  return (
    <>
      {useUIPermissionCheck(
        adminRole,
        permissionsList,
        permissionItems.toggleActiveItem
      ) && (
        <DropdownMenuItem className="flex items-center justify-between gap-2 text-[12px]">
          <span>Active</span>
          <SwitchButton
            args={toggleActiveArgs}
            status={activeStatus}
            apiCallback={actionFunctions.toggleActiveItemFnc}
          />
        </DropdownMenuItem>
      )}
    </>
  );
};

// Featured Status Toggle Switch
const GetFeaturedStatusToggleSwitch = ({
  adminRole,
  permissionsList,
  permissionItems,
  toggleFeaturedArgs = [],
  featuredStatus,
  actionFunctions = {},
}) => {
  return (
    <>
      {useUIPermissionCheck(
        adminRole,
        permissionsList,
        permissionItems.toggleFeaturedItem
      ) && (
        <DropdownMenuItem className="flex items-center justify-between gap-2 text-[12px]">
          <span>Featured</span>
          <SwitchButton
            args={toggleFeaturedArgs}
            status={featuredStatus}
            apiCallback={actionFunctions.toggleFeaturedItemFnc}
          />
        </DropdownMenuItem>
      )}
    </>
  );
};

// Default Status Toggle Switch
const GetDefaultStatusToggleSwitch = ({
  adminRole,
  permissionsList,
  permissionItems,
  toggleDefaultArgs = [],
  defaultStatus,
  actionFunctions = {},
}) => {
  return (
    <>
      {useUIPermissionCheck(
        adminRole,
        permissionsList,
        permissionItems.toggleDefaultItem
      ) && (
        <DropdownMenuItem className="flex items-center justify-between gap-2 text-[12px]">
          <span>Default</span>
          <SwitchButton
            args={toggleDefaultArgs}
            status={defaultStatus}
            apiCallback={actionFunctions.toggleDefaultItemFnc}
          />
        </DropdownMenuItem>
      )}
    </>
  );
};

const CommonTableActions = ({
  actions = {
    edit: false,
    delete: false,
    active: false,
    feature: false,
    default: false,
  },

  editRoute = "",
  targetType = "",
  targetName = "",
  deleteArgs = [],
  toggleActiveArgs = [],
  toggleFeaturedArgs = [],
  toggleDefaultArgs = [],
  activeStatus = false,
  featuredStatus = false,
  defaultStatus = false,
  actionFunctions,
  permissionItems,
  adminRole,
  permissionsList,
}) => {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger>
        <div className="flex justify-end items-center rounded-full bg-[#000]/10 dark:bg-[#fff]/10 p-2">
          <ListFilterPlus className="size-[16px]" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={`${globalStyleObj.backgroundLight900Dark200} border dark:border-[#fff]/10 font-poppins-rg text-dark-weight-400 dark:text-light-weight-450 min-w-[150px]`}
      >
        <DropdownMenuLabel className="dark:text-light-weight-800 tracking-wider text-[13px]">
          Actions
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Edit Action */}
        {actions.edit && (
          <GetEditAction
            adminRole={adminRole}
            permissionsList={permissionsList}
            permissionItems={permissionItems}
            editRoute={editRoute}
          />
        )}

        {/* Delete Action */}
        {actions.delete && (
          <GetDeleteAction
            adminRole={adminRole}
            permissionsList={permissionsList}
            permissionItems={permissionItems}
            targetType={targetType}
            targetName={targetName}
            deleteArgs={deleteArgs}
            actionFunctions={actionFunctions}
          />
        )}

        {/* Active Status Toggle Switch */}
        {actions.active && (
          <GetActiveStatusToggleSwitch
            adminRole={adminRole}
            permissionsList={permissionsList}
            permissionItems={permissionItems}
            toggleActiveArgs={toggleActiveArgs}
            activeStatus={activeStatus}
            actionFunctions={actionFunctions}
          />
        )}

        {/* Featured Status Toggle Switch */}
        {actions.feature && (
          <GetFeaturedStatusToggleSwitch
            adminRole={adminRole}
            permissionsList={permissionsList}
            permissionItems={permissionItems}
            toggleFeaturedArgs={toggleFeaturedArgs}
            featuredStatus={featuredStatus}
            actionFunctions={actionFunctions}
          />
        )}

        {/* Default Status Toggle Switch */}
        {actions.default && (
          <GetDefaultStatusToggleSwitch
            adminRole={adminRole}
            permissionsList={permissionsList}
            permissionItems={permissionItems}
            toggleDefaultArgs={toggleDefaultArgs}
            defaultStatus={defaultStatus}
            actionFunctions={actionFunctions}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CommonTableActions;
